import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  Chip,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HubIcon from '@mui/icons-material/Hub';
import GoogleIcon from '@mui/icons-material/Google';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useDispatch } from 'react-redux';
import { logout } from 'src/modules/auth/store/authActions';
import ConnectModal from 'src/components/App/ConnectModal';

const navItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, link: '/dashboard' },
  { text: 'Appointments', icon: <CalendarMonthIcon />, link: '/appointments' },
  { text: 'Tasks & Follow-ups', icon: <AssignmentIcon />, link: '/tasks' },
  { text: 'Patients', icon: <PeopleAltIcon />, link: '/patients' },
  { text: 'Notifications', icon: <NotificationsIcon />, link: '/notifications' },
];

const Sidebar = ({ open, onClose, isMobile = false }) => {
  const theme = useTheme();
  const location = useLocation();
  const dispatch = useDispatch();

  const [openConnectModal, setOpenConnectModal] = useState(false);

  const isDark = theme.palette.mode === 'dark';
  const staffName = localStorage.getItem('staffName') || localStorage.getItem('STAFFNAME') || 'Staff Member';
  const staffEmail = (localStorage.getItem('staffEmail') || localStorage.getItem('STAFFEMAIL') || '').toLowerCase();
  const isSanginiOnly = staffEmail.includes('sangini') || staffEmail === 'tapan@sanginihospital.com';

  const visibleNavItems = isSanginiOnly
    ? [{ text: 'Appointments', icon: <CalendarMonthIcon />, link: '/appointments' }]
    : navItems;

  const selectedClinicStr = localStorage.getItem('selectedClinic');
  let selectedClinic = null;
  try {
    selectedClinic = selectedClinicStr ? JSON.parse(selectedClinicStr) : null;
  } catch (e) {
    selectedClinic = null;
  }

  const handleLogout = () => {
    dispatch(logout());
  };

  const sidebarContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        transition: 'background-color 0.2s ease, border-color 0.2s ease',
      }}
    >
      {/* Brand & Logo Header */}
      <Box
        sx={{
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '10px',
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText || '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          >
            <LocalHospitalIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: theme.palette.text.primary, lineHeight: 1.2 }}>
              MyCliniq
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, fontSize: '11px' }}>
              STAFF PORTAL
            </Typography>
          </Box>
        </Box>

        {isMobile && (
          <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.text.secondary }}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>

      {/* Selected Clinic Badge */}
      {selectedClinic && (
        <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
          <Box
            sx={{
              p: 1.2,
              borderRadius: '10px',
              bgcolor: theme.palette.background.subtle,
              border: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: '#22C55E',
                boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.2)',
                flexShrink: 0,
              }}
            />
            <Box sx={{ overflow: 'hidden' }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '12px',
                }}
              >
                {selectedClinic.clinicName}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '10px' }}>
                Active Clinic
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Navigation List */}
      <Box sx={{ flexGrow: 1, px: 2, py: 1.5, overflowY: 'auto' }}>
        <Typography
          variant="caption"
          sx={{
            px: 1.5,
            py: 1,
            display: 'block',
            fontWeight: 700,
            color: theme.palette.text.secondary,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            fontSize: '10.5px',
          }}
        >
          Staff Navigation
        </Typography>

        <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 0 }}>
          {visibleNavItems.map((item) => {
            const isActive =
              location.pathname === item.link ||
              (item.link === '/dashboard' && location.pathname === '/');

            return (
              <ListItem
                key={item.text}
                component={Link}
                to={item.link}
                onClick={isMobile ? onClose : undefined}
                sx={{
                  borderRadius: '12px',
                  px: 2,
                  py: 1.2,
                  textDecoration: 'none',
                  color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                  bgcolor: isActive
                    ? alpha(theme.palette.primary.main, isDark ? 0.2 : 0.08)
                    : 'transparent',
                  fontWeight: isActive ? 700 : 500,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: isActive
                      ? alpha(theme.palette.primary.main, isDark ? 0.28 : 0.14)
                      : alpha(theme.palette.text.primary, 0.04),
                    color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 36,
                    color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '14px',
                    fontWeight: isActive ? 700 : 500,
                  }}
                />
                {item.badge && (
                  <Chip
                    label={item.badge}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '10px',
                      fontWeight: 800,
                      bgcolor: isActive ? theme.palette.primary.main : theme.palette.background.subtle,
                      color: isActive ? theme.palette.primary.contrastText : theme.palette.text.secondary,
                    }}
                  />
                )}
              </ListItem>
            );
          })}
        </List>

        {/* Connect With Us Card in Sidebar */}
        <Box sx={{ mt: 2.5, px: 0.5 }}>
          <Box
            onClick={() => {
              if (isMobile) onClose();
              setOpenConnectModal(true);
            }}
            sx={{
              p: 1.8,
              borderRadius: '14px',
              bgcolor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.15)}`,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.1),
                borderColor: theme.palette.primary.main,
                transform: 'translateY(-2px)',
                boxShadow: isDark ? '0 8px 20px rgba(0,0,0,0.4)' : '0 8px 20px rgba(0,0,0,0.05)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    bgcolor: theme.palette.primary.main,
                    color: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <HubIcon sx={{ fontSize: 16 }} />
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.palette.text.primary, fontSize: '12.5px' }}>
                  Connect with Us!
                </Typography>
              </Box>
              <Chip
                label="Community"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '9.5px',
                  fontWeight: 800,
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  color: theme.palette.primary.main,
                }}
              />
            </Box>

            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '11px', display: 'block', mb: 1.2 }}>
              Google Reviews, Facebook, Instagram & LinkedIn
            </Typography>

            {/* Social Icons Mini Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: '#4285F4', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <GoogleIcon sx={{ fontSize: 13 }} />
              </Box>
              <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: '#1877F2', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FacebookIcon sx={{ fontSize: 14 }} />
              </Box>
              <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: '#E1306C', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <InstagramIcon sx={{ fontSize: 14 }} />
              </Box>
              <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: '#0A66C2', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LinkedInIcon sx={{ fontSize: 14 }} />
              </Box>
              <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 700, ml: 'auto', fontSize: '11px' }}>
                Open ↗
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Staff Profile Quick Info & Logout */}
      <Divider sx={{ borderColor: theme.palette.divider }} />
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: theme.palette.background.subtle,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText || '#FFFFFF',
              fontWeight: 700,
              fontSize: '14px',
            }}
          >
            {staffName.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ maxWidth: 120 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: theme.palette.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {staffName}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '11px' }}>
              Staff Desk
            </Typography>
          </Box>
        </Box>

        <Tooltip title="Logout">
          <IconButton onClick={handleLogout} size="small" sx={{ color: theme.palette.text.secondary, '&:hover': { color: '#DC2626' } }}>
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Connect With Us Modal */}
      <ConnectModal
        open={openConnectModal}
        onClose={() => setOpenConnectModal(false)}
      />
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: 260, border: 'none', bgcolor: theme.palette.background.paper },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Box
      component="nav"
      sx={{
        width: 260,
        flexShrink: 0,
        height: '100vh',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      {sidebarContent}
    </Box>
  );
};

export default Sidebar;
